import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Form, Tabs } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import {
  MODULES,
  PLAN_NAMES,
  PLAY_PLAN_SLUGS,
  ROUTES,
} from '../../../../../common/constants';
import { getPlayPlanLabel } from '../../../../../common/utils';
import GoBackButton from '../../../../../components/GoBackButton';
import LoaderComponent from '../../../../../components/LoaderComponent';
import Portal from '../../../../../components/Portal';
import PopularPlanSelect from '../../../components/PopularPlanSelect';
import SubscriptionForm from '../../../components/SubscriptionForm';
import {
  SYNC_SUBSCRIPTION_PLANS,
  UPDATE_SUBSCRIPTION_PLAN,
} from '../../../graphql/Mutations';
import { SUBSCRIPTION_PLANS_ADMIN } from '../../../graphql/Queries';

const planVersion = process.env.REACT_APP_PLAN_VERSION;

const PlayPlanTabs = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  const { dispatch } = useContext(AppContext);
  const [isSyncPlanLoading, setIsSyncPlanLoading] = useState(false);
  const [isSync, setIsSync] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('');
  const [planList, setPlanList] = useState([]);
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);

  const [subscriptionPlansAdmin] = useLazyQuery(SUBSCRIPTION_PLANS_ADMIN, {
    onCompleted: (response) => {
      const rawData = response?.subscriptionPlansAdmin?.data || [];

      const planOrder = [
        PLAY_PLAN_SLUGS.PLAY_MAX_ANNUALLY.value,
        PLAY_PLAN_SLUGS.PLAY_MAX_QUARTERLY.value,
        PLAY_PLAN_SLUGS.PLAY_PLUS_ANNUALLY.value,
        PLAY_PLAN_SLUGS.PLAY_PLUS_QUARTERLY.value,
        PLAY_PLAN_SLUGS.PLAY_MINI_ANNUALLY.value,
      ];

      const sortedPlans = rawData.sort((a, b) => {
        const indexA = planOrder.indexOf(a?.slug || a?.value || a?.key);
        const indexB = planOrder.indexOf(b?.slug || b?.value || b?.key);

        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

      setPlanList(sortedPlans);
      setIsPlanLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [updateSubscriptionPlan] = useMutation(UPDATE_SUBSCRIPTION_PLAN, {
    onError: () => {},
  });

  const [syncSubscriptionPlans] = useMutation(SYNC_SUBSCRIPTION_PLANS, {
    onError: () => {},
  });

  const handleSync = async () => {
    setIsSyncPlanLoading(true);
    const res = await syncSubscriptionPlans();
    if (res?.data) setIsSync(!isSync);
    setIsSyncPlanLoading(false);
  };

  useEffect(() => {
    setIsPlanLoading(true);
    subscriptionPlansAdmin({
      variables: {
        filter: {
          name: PLAN_NAMES?.PLAY?.value,
          planVersion: `v${planVersion}`,
        },
      },
    });
  }, [isEdit]);

  const onFinish = async (values) => {
    setBtnLoading(true);
    const planDetails = planList?.find((plan) => plan?.slug === activeTabKey);
    const response = await updateSubscriptionPlan({
      variables: {
        where: {
          id: planDetails?.id,
        },
        data: {
          moneyBackGuarantee: parseInt(values?.moneyBackGuarantee, 10),
          sameDayDelivery: values?.sameDayDelivery,
          maxBigToyAllowed: parseInt(values?.maxBigToyAllowed, 10),
          maxToyHoldingValueAllowed: parseInt(
            values?.maxToyHoldingValueAllowed,
            10,
          ),
          bonusDeliveries: parseInt(values?.bonusDeliveries, 10),
          totalKeepCountPerMonth: parseInt(values?.holdingToys, 10),
          totalFreeDeliveryPerMonth: parseInt(values?.freeDeliveries, 10),
          trialApplied: values?.trialApplied,
          membershipFeesApplied: values?.membershipFeesApplied,
          securityDepositApplied: values?.securityDepositApplied,
          trialDays: parseInt(values?.trialDays, 10),
          trialAmount: parseInt(values?.trialAmount, 10),
          actualChargesForSecurityDeposit: parseInt(
            values?.actualChargesForSecurityDeposit,
            10,
          ),
          damageWaiverFeesApplied: values?.damageWaiverFeesApplied,
          damageWaiverFees: parseInt(values?.damageWaiverFees, 10),
          actualChargesForDamageWaiverFees: parseInt(
            values?.actualChargesForDamageWaiverFees,
            10,
          ),
          actualChargesForMemberShipFees: parseInt(
            values?.actualChargesForMemberShipFees,
            10,
          ),
          isUnlimitedFreeDelivery: values?.isUnlimitedFreeDelivery,
          config: {
            phonePeOneTimePayment: values?.phonePeOneTimePayment,
            phonePeRecurringPayment: values?.phonePeRecurringPayment,
            razorPayOneTimePayment: values?.razorPayOneTimePayment,
            razorPayRecurringCardPayment: values?.razorPayRecurringCardPayment,
            razorPayRecurringUpiPayment: values?.razorPayRecurringUpiPayment,
            stripeRecurringCardPayment: values?.stripeRecurringCardPayment,
          },
        },
      },
    });
    if (response?.data) {
      setIsPlanLoading(true);
      setBtnLoading(false);
      setIsDisabled(true);
      dispatch({
        type: 'SET_SHOW_PROMPT',
        data: false,
      });
      subscriptionPlansAdmin({
        variables: {
          filter: {
            name: PLAN_NAMES?.PLAY?.value,
            planVersion: `v${planVersion}`,
          },
        },
      });
    }
  };

  const handleDiscard = () => {
    setIsPlanLoading(true);
    subscriptionPlansAdmin({
      variables: {
        filter: {
          name: PLAN_NAMES?.PLAY?.value,
          planVersion: `v${planVersion}`,
        },
      },
    });
    setIsDisabled(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
  };

  const getTabKey = (plan) => plan?.slug;

  const tabList = planList?.map((plan) => ({
    key: getTabKey(plan),
    label: getPlayPlanLabel(plan),
    children: (
      <SubscriptionForm
        form={form}
        planName={plan?.name}
        planType={plan?.type}
        isView={!isEdit}
        planDetails={plan}
        onFinish={onFinish}
        setIsDisabled={setIsDisabled}
        isNewPlan
      />
    ),
  }));

  useEffect(() => {
    const pathSegments = location?.pathname?.split('/') || [];
    const activeTab = tabList?.find((tab) => pathSegments?.includes(tab?.key));
    setIsEdit(location?.pathname?.includes('/edit'));
    setActiveTabKey(activeTab?.key);
    const playPlanPath = `${ROUTES.SUBSCRIPTION_PLANS}${ROUTES.PLAY_PLANS}`;
    if (location?.pathname === playPlanPath) {
      if (activeTab) {
        history.push(
          `${playPlanPath}${activeTab?.key ? `/${activeTab?.key}` : ''}`,
        );
      } else {
        history.push(
          `${playPlanPath}${tabList?.[0]?.key ? `/${tabList?.[0]?.key}` : ''}`,
        );
      }
    }
  }, [location, tabList]);
  const onTabChange = (key) => {
    setActiveTabKey(key);
    const path = `${ROUTES.SUBSCRIPTION_PLANS}${ROUTES.PLAY_PLANS}/${key}${
      isEdit ? '/edit' : ''
    }`;
    history.push(path);
  };
  return (
    <>
      {!isEdit ? (
        <>
          <Portal portalId="header-title-content">
            <div className="portal-header">{MODULES?.PLAY_PLANS}</div>
          </Portal>
          <Portal portalId="header-right-content">
            <div className="header-btn-wrapper">
              <PopularPlanSelect />
              <Button
                loading={isSyncPlanLoading}
                className="common-button mr-16"
                size="middle"
                onClick={handleSync}
              >
                Sync Plan
              </Button>
              <Button
                className="common-button mr-16"
                size="middle"
                onClick={() => {
                  setIsEdit(true);
                  history?.push(
                    `${ROUTES?.SUBSCRIPTION_PLANS}${ROUTES?.PLAY_PLANS}/${activeTabKey}/edit`,
                  );
                }}
              >
                Edit Plan
              </Button>
            </div>
          </Portal>
          <Portal portalId="header-right-content-phones">
            <div className="header-btn-wrapper">
              <PopularPlanSelect />
              <Button
                loading={isSyncPlanLoading}
                className="common-button mr-16"
                size="middle"
                onClick={handleSync}
              >
                Sync Plan
              </Button>
              <Button
                className="common-button mr-16"
                size="middle"
                onClick={() => {
                  setIsEdit(true);
                  history?.push(
                    `${ROUTES?.SUBSCRIPTION_PLANS}${ROUTES?.PLAY_PLANS}/${activeTabKey}/edit`,
                  );
                }}
              >
                Edit Plan
              </Button>
            </div>
          </Portal>
        </>
      ) : (
        <>
          <Portal portalId="header-title-content">
            <GoBackButton customLink={ROUTES?.SUBSCRIPTION_PLANS} />
            <div className="portal-header">Edit {MODULES?.PLAY_PLANS}</div>
          </Portal>
          <Portal portalId="header-right-content">
            <div className="header-btn-wrapper">
              <Button
                type="primary"
                className="common-button mr-16"
                size="middle"
                disabled={btnLoading || isDisabled}
                onClick={handleDiscard}
              >
                Discard Changes
              </Button>
              <Button
                className="common-button"
                size="middle"
                onClick={form.submit}
                disabled={isDisabled}
              >
                Save
              </Button>
            </div>
          </Portal>
          <Portal portalId="header-right-content-phones">
            <div className="header-btn-wrapper">
              <Button
                type="primary"
                className="common-button mr-16"
                size="middle"
                disabled={btnLoading || isDisabled}
                onClick={handleDiscard}
              >
                Discard Changes
              </Button>
              <Button
                className="common-button"
                size="middle"
                onClick={form.submit}
                disabled={isDisabled}
              >
                Save
              </Button>
            </div>
          </Portal>
        </>
      )}
      <div className="card-container">
        {isPlanLoading ? (
          <LoaderComponent
            size="large"
            setHeight="60"
            spinning={isPlanLoading}
          />
        ) : (
          <Tabs
            activeKey={activeTabKey}
            type="card"
            className="full-width"
            onChange={(key) => onTabChange(key)}
            items={tabList}
            destroyInactiveTabPane
          />
        )}
      </div>
    </>
  );
};

export default PlayPlanTabs;
